import { useState } from 'react';
import { ref, set } from 'firebase/database';
import { database } from '../../firebase';
import evolutionsData from '../../data/evolutions.json';
import movesDataRaw from '../../data/moves.json';
import itemsDataRaw from '../../data/items.json';
import typesDataRaw from '../../data/types.json';
import { getBaseStatPatch } from '../../utils/pokemonBaseStats';
import { getPokemonDisplayParts } from '../../utils/pokemonDisplayName';
import {
  getWurmpleEvolutionTarget,
  isWurmple,
  withWurmpleEvolutionId
} from '../../utils/wurmpleEvolution';
import { getAlcremieImage, getAlcremieShapeForItem } from '../../utils/alcremieFlavors';
import { getAbilityKoreanName } from '../../utils/abilityUtils';

const allMovesData = Array.isArray(movesDataRaw) ? movesDataRaw : movesDataRaw.moves || [];
const allItemsData = Array.isArray(itemsDataRaw) ? itemsDataRaw : itemsDataRaw.items || [];
const allTypesData = Array.isArray(typesDataRaw) ? typesDataRaw : typesDataRaw.types || [];

// 포켓몬 개체의 type/type2는 한글명("악", "에스퍼" 등)으로 저장되는데,
// evolutions.json의 조건(partyType 등)은 영문 슬러그("dark", "psychic")를 쓴다.
// 어느 쪽 표기로 와도 같은 타입이면 매칭되도록 정규화한다.
const normalizeTypeToken = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  const match = allTypesData.find((t) => (
    String(t.name || '').toLowerCase() === raw ||
    String(t.nameEn || '').toLowerCase() === raw ||
    String(t.nameKo || '') === String(value || '').trim() ||
    String(t.nameJa || '') === String(value || '').trim()
  ));
  return match ? String(match.name || '').toLowerCase() : raw;
};

const pokemonHasType = (pokemon, typeToken) => {
  if (!pokemon) return false;
  const target = normalizeTypeToken(typeToken);
  if (!target) return false;
  return normalizeTypeToken(pokemon.type) === target
    || normalizeTypeToken(pokemon.type2) === target;
};

const getBaseName = (pokemon) => getPokemonDisplayParts(pokemon).name;

// 아이템 이름 정규화 (한글명/영문명/슬러그 어느 쪽으로 와도 비교 가능하도록)
const normalizeItemToken = (value) => String(value || '')
  .toLowerCase()
  .replace(/[\s-_]/g, '')
  .replace(/의돌/g, '')
  .replace(/stone/g, '');

// pokemon.heldItem은 UI에서 아이템의 한글 표시명(item.name)으로 저장되므로,
// evolutions.json의 영문 슬러그(예: "razor-fang")와 직접 비교할 수 없다.
// items.json에서 매칭되는 아이템을 찾아 정규화된 토큰으로 비교한다.
const heldItemMatchesCondition = (heldItem, conditionHeldItem) => {
  if (!conditionHeldItem) return true;
  if (!heldItem) return false;

  const normalizedCondition = normalizeItemToken(conditionHeldItem);
  const normalizedHeld = normalizeItemToken(heldItem);
  if (normalizedHeld === normalizedCondition) return true;

  const matchedItem = allItemsData.find((item) => {
    const nameToken = normalizeItemToken(item.name);
    const nameEnToken = normalizeItemToken(item.nameEn);
    return nameToken === normalizedHeld || nameEnToken === normalizedHeld;
  });

  return normalizeItemToken(matchedItem?.nameEn) === normalizedCondition;
};

export const useEvolution = (currentUser, updateCurrentUser, allPokemonMaster, updateHistoryField, updateOwnedPokemonByUniqueId) => {
  const [evolutionModal, setEvolutionModal] = useState(null);
  // 마빌크 → 마휘핑: 어떤 맛(크림)으로 진화할지 고르는 중일 때 {pokemon, evolution, shapeId}
  const [alcremieFlavorPending, setAlcremieFlavorPending] = useState(null);

  const toPokemonNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const getPokemonNumberCandidates = (pokemon = {}) => {
    const currentNumber = toPokemonNumber(pokemon.number);
    const pokemonId = toPokemonNumber(pokemon.pokemonId || pokemon.id);
    const originalNumber = toPokemonNumber(pokemon.originalNumber);
    const isRegionalOrForm = Boolean(pokemon.regionalForm || pokemon.formVariant);

    if (isRegionalOrForm) {
      const formNumbers = [currentNumber, pokemonId]
        .filter((number) => number !== null && number !== originalNumber);
      if (formNumbers.length > 0) {
        return formNumbers.filter((number, index, numbers) => numbers.indexOf(number) === index);
      }

      // 폼(currentNumber/pokemonId)이 실제 숫자 도감번호를 갖지 못하는 경우(플라베베/플라엣테
      // 꽃 색깔 폼처럼 id가 "pokemon-form-XXXXX" 같은 문자열뿐인 코스메틱 폼)엔 originalNumber로
      // 폴백한다. 그렇지 않으면 candidates가 빈 배열이 되어 evolutions.json의 원종 번호(예: 669)
      // 기준 진화 조건과 절대 매칭되지 않아, 빨간 꽃(진짜 숫자 번호를 쓰는 기본형)만 진화하고
      // 나머지 색깔 폼은 영원히 진화하지 못하는 문제가 있었다.
      return [currentNumber, pokemonId, originalNumber]
        .filter((number, index, numbers) => number !== null && numbers.indexOf(number) === index);
    }

    if (currentNumber !== null) return [currentNumber];

    return [pokemon.originalNumber, pokemonId]
      .map(toPokemonNumber)
      .filter((number, index, numbers) => number !== null && numbers.indexOf(number) === index);
  };

  const findEvolutionForPokemon = (pokemon, predicate = () => true) => {
    const candidates = getPokemonNumberCandidates(pokemon);
    return evolutionsData.evolutions.find((evo) =>
      candidates.includes(toPokemonNumber(evo.from)) && predicate(evo)
    );
  };

  const findAllEvolutionsForPokemon = (pokemon) => {
    const candidates = getPokemonNumberCandidates(pokemon);
    const wurmpleTarget = getWurmpleEvolutionTarget(pokemon);
    const seenTargets = new Set();
    return evolutionsData.evolutions
      .filter((evo) => candidates.includes(toPokemonNumber(evo.from)))
      .filter((evo) => wurmpleTarget === null || toPokemonNumber(evo.to) === wurmpleTarget)
      .filter((evo) => {
        const key = `${evo.to}-${evo.toName || ''}`;
        if (seenTargets.has(key)) return false;
        seenTargets.add(key);
        return true;
      });
  };

  const findPokemonTemplateByNumber = (number) => {
    const targetNumber = toPokemonNumber(number);
    return (
      allPokemonMaster.find((pokemon) => toPokemonNumber(pokemon.number) === targetNumber) ||
      allPokemonMaster.find((pokemon) => toPokemonNumber(pokemon.id) === targetNumber) ||
      allPokemonMaster.find((pokemon) => !pokemon.regionalForm && toPokemonNumber(pokemon.originalNumber) === targetNumber) ||
      allPokemonMaster.find((pokemon) => toPokemonNumber(pokemon.originalNumber) === targetNumber)
    );
  };

  // 플라베베→플라엣테처럼 꽃 색깔 등 "코스메틱 폼"이 진화 후에도 같은 색으로 이어지도록,
  // findPokemonTemplateByNumber(evolution.to)로 아무 폼이나(기본적으로 첫 매치=빨강) 잡기 전에
  // 진화 전 개체의 formVariant 접미사("flabebe-blue"의 "-blue")와 같은 접미사를 가진 진화형
  // 템플릿("floette-blue")이 있는지 먼저 찾는다. 없으면(=색깔 폼이 아니거나 매칭 템플릿이 없으면)
  // 기존 방식(숫자 도감번호 매칭)으로 폴백한다.
  const findEvolvedTemplateForPokemon = (pokemon, evolution) => {
    const fromNameEn = (evolution.fromName || '').toLowerCase();
    const toNameEn = (evolution.toName || '').toLowerCase();
    const sourceVariant = (pokemon.formVariant || pokemon.species || pokemon.nameEn || '').toLowerCase();

    // 배쓰나이(흰색근) → 대쓰여너: 대쓰여너는 성별로 폼(수컷 902 / 암컷 10248)이 갈리고
    // 스탯·스프라이트도 다르므로, 진화 전 개체의 성별에 맞는 템플릿을 골라준다.
    if (toNameEn === 'basculegion') {
      const wantFemale = String(pokemon.gender || '').toLowerCase() === 'female';
      const genderedTemplate = allPokemonMaster.find((p) =>
        (p.nameEn || '').toLowerCase() === (wantFemale ? 'basculegion-female' : 'basculegion-male')
      );
      if (genderedTemplate) return genderedTemplate;
    }

    if (fromNameEn && toNameEn && sourceVariant.startsWith(`${fromNameEn}-`)) {
      const suffix = sourceVariant.slice(fromNameEn.length);
      const expectedVariant = `${toNameEn}${suffix}`;
      const variantTemplate = allPokemonMaster.find((p) =>
        (p.nameEn || '').toLowerCase() === expectedVariant ||
        (p.species || '').toLowerCase() === expectedVariant
      );
      if (variantTemplate) return variantTemplate;
    }

    return findPokemonTemplateByNumber(evolution.to);
  };

  const ensureStoredWurmpleEvolutionId = (pokemon) => {
    if (!isWurmple(pokemon)) return pokemon;
    const preparedPokemon = withWurmpleEvolutionId(pokemon);

    if (preparedPokemon.wurmpleEvolutionId === pokemon.wurmpleEvolutionId) {
      return preparedPokemon;
    }

    const updatedCaught = (currentUser.caughtPokemon || []).map(p =>
      p?.uniqueId === pokemon.uniqueId ? preparedPokemon : p
    );
    const updates = { caughtPokemon: updatedCaught };

    if (currentUser.partnerPokemon?.uniqueId === pokemon.uniqueId) {
      updates.partnerPokemon = {
        ...currentUser.partnerPokemon,
        wurmpleEvolutionId: preparedPokemon.wurmpleEvolutionId
      };
    }

    updateCurrentUser(updates);
    return preparedPokemon;
  };

  // 레벨업이 필요한 조건 타입 (아이템/교환 진화는 즉시 적용)
  const LEVEL_UP_REQUIRED_CONDITIONS = new Set(['friendship', 'moveUsage']);
  const isLevelUpRequiredCondition = (condition) =>
    LEVEL_UP_REQUIRED_CONDITIONS.has(condition?.type) ||
    condition?.minBeauty !== undefined ||
    (!condition?.type && condition?.knownMove);

 // 단일 진화 조건 충족 여부 확인 (내부 헬퍼)
  const checkSingleEvolutionCondition = (pokemon, evolution, { onLevelUp = false } = {}) => {
    const { condition } = evolution;

    // 레벨업 필요 조건: 레벨업 시 또는 이미 레벨업으로 조건 충족된 경우만 허용
    if (isLevelUpRequiredCondition(condition) && !onLevelUp && !pokemon.evolutionReady) return false;

    if (condition.type === 'level') {
      if (pokemon.level < condition.level) return false;

      if (condition.ability) {
        const hasAbility = pokemon.ability === condition.ability || pokemon.abilityEn === condition.ability;
        console.log('💡 특성 조건:', condition.ability, '보유:', pokemon.ability, '충족:', hasAbility);
        if (!hasAbility) return false;
      }

      if (condition.timeOfDay) {
        const hour = (new Date().getUTCHours() + 9) % 24;
        const currentTime = hour >= 6 && hour < 18 ? 'day' : 'night';
        console.log('⏰ 시간 조건:', currentTime, '필요:', condition.timeOfDay);
        if (currentTime !== condition.timeOfDay) return false;
      }

      if (condition.partyType) {
        // 개체의 type은 한글("악")로 저장되고 조건은 영문("dark")이라 직접 비교하면
        // 절대 매칭되지 않는다(판짱→부란다 진화 불가 회귀). 타입 토큰을 정규화해서 비교한다.
        const hasType = (currentUser.caughtPokemon || [])
          .slice(0, 6)
          .some(p => pokemonHasType(p, condition.partyType));
        if (!hasType) return false;
      }

      if (condition.knownMove) {
        const hasMove = pokemon.moves?.some(
          m => m.moveId === condition.knownMove || m.name?.toLowerCase() === condition.knownMove
        );
        if (!hasMove) return false;
      }

      if (condition.heldItem && !heldItemMatchesCondition(pokemon.heldItem, condition.heldItem)) {
        return false;
      }

      return true;
    }

    if (condition.type === 'friendship') {
      if ((pokemon.friendship || 0) < condition.friendship) return false;

      if (condition.timeOfDay) {
        const hour = (new Date().getUTCHours() + 9) % 24;
        const currentTime = hour >= 6 && hour < 18 ? 'day' : 'night';
        if (currentTime !== condition.timeOfDay) return false;
      }

      if (condition.knownMoveType) {
        const targetType = condition.knownMoveType.toLowerCase();
        const hasFairyMove = pokemon.moves?.some(m => {
          const moveId = m.moveId || m.id || '';
          const moveData = allMovesData.find(md => md.id === moveId);
          const moveType = (moveData?.type || m.type || m.moveType || '').toLowerCase();
          return moveType === targetType;
        });
        if (!hasFairyMove) return false;
      }

      return true;
    }

    if (condition.type === 'battleCrit') {
      return (pokemon.lastBattleCritCount || 0) >= (condition.count || 1);
    }

    if (condition.minBeauty !== undefined) {
      return (pokemon.condition?.beauty || 0) >= condition.minBeauty;
    }

    if (condition.type === 'moveUsage') {
      const moveId = (condition.move || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const used = pokemon.moveUsage?.[moveId] || 0;
      return used >= (condition.count || 1);
    }

    // type 없이 knownMove만 있는 경우 (예: 꼬지지→미스터마임, 꼬지지→나무킹)
    // 레벨업 시 해당 기술을 알고 있으면 진화
    if (!condition.type && condition.knownMove) {
      const hasMove = pokemon.moves?.some(
        m => m.moveId === condition.knownMove || m.name?.toLowerCase() === condition.knownMove
      );
      return !!hasMove;
    }

    return false;
  };

 // 진화 가능 여부 확인 (onLevelUp: 레벨업 시 호출 여부)
  const checkEvolution = (pokemon, { onLevelUp = false } = {}) => {
    console.log('🔍 checkEvolution 호출:', pokemon.name, pokemon.number, 'Lv.', pokemon.level);

    if (!pokemon) {
      console.log('❌ pokemon이 없음');
      return null;
    }

    // 해당 포켓몬의 모든 진화 경로 찾기 (특성 조건이 있는 것이 우선 배치됨)
    const evolutions = findAllEvolutionsForPokemon(pokemon);

    console.log('📋 찾은 진화 경로 수:', evolutions.length);

    if (evolutions.length === 0) {
      console.log('❌ 진화 정보 없음 (이 포켓몬은 진화하지 않거나 최종 진화형)');
      return null;
    }

    for (const evolution of evolutions) {
      console.log('📝 진화 조건 체크:', evolution.toName, evolution.condition);
      if (checkSingleEvolutionCondition(pokemon, evolution, { onLevelUp })) {
        console.log('🎉 모든 조건 충족! 진화 가능!', evolution.toName);
        return evolution;
      }
    }

    // 아이템 진화는 별도 함수에서 처리
    // 교환 진화는 나중에 구현

    console.log('❌ 최종: 진화 조건 미충족');
    return null;
  };

  // 레벨업 시 진화 체크
  const checkEvolutionOnLevelUp = (pokemon) => {
    console.log('🔍 진화 체크 시작:', pokemon.name, 'Lv.', pokemon.level);
    
    // 변함없는돌을 지니고 있으면 진화 체크 안함
    if (pokemon.heldItem === 'everstone' || pokemon.heldItem === '변함없는돌') {
      console.log('🪨 변함없는돌 착용 중 - 진화 불가');
      return false;
    }
    
    // 이미 진화를 거부한 적이 있으면 자동 모달 띄우지 않음
    if (pokemon.evolutionCancelled) {
      console.log('❌ 이전에 진화를 취소함 - 자동 모달 안띄움');
      return false;
    }
    
    const preparedPokemon = ensureStoredWurmpleEvolutionId(pokemon);
    const evolution = checkEvolution(preparedPokemon, { onLevelUp: true });
    console.log('🎯 진화 정보:', evolution);
    
    if (evolution) {
      console.log('✨ 진화 가능! 모달 띄우기');

      const evolvedPokemonData = findEvolvedTemplateForPokemon(preparedPokemon, evolution);

      if (!evolvedPokemonData) {
        console.log('❌ 진화할 포켓몬 데이터 없음');
        return false;
      }

      // 레벨업으로 조건 충족됨을 기록 → 이후 수동 진화 허용
      if (!pokemon.evolutionReady) {
        const updatedCaught = (currentUser.caughtPokemon || []).map(p =>
          p?.uniqueId === preparedPokemon.uniqueId ? { ...p, ...preparedPokemon, evolutionReady: true } : p
        );
        updateCurrentUser({ caughtPokemon: updatedCaught });
      }

      setEvolutionModal({
        show: true,
        pokemon: { ...preparedPokemon, evolutionReady: true },
        evolution: evolution,
        fromPokemon: preparedPokemon,
        toPokemon: evolvedPokemonData,
        isItemEvolution: false
      });
      return true;
    }
    
    console.log('❌ 진화 조건 미충족');
    return false;
  };

  // 아이템으로 진화
  const evolveWithItem = (pokemon, itemName) => {
    console.log('🔥 evolveWithItem 호출');
    console.log('🔥 pokemon.number:', pokemon.number);
    console.log('🔥 itemName:', itemName);
    
    // 아이템 이름 정규화 (공백, 하이픈 제거, 소문자, 한글 제거)
    const normalizeItemName = (name) => {
      if (!name) return '';
      return name.toLowerCase()
        .replace(/[\s-_]/g, '')  // 공백, 하이픈, 언더스코어 제거
        .replace(/stone/g, '')    // 'stone' 제거
        .replace(/의돌/g, '')     // '의돌' 제거
        .replace(/[가-힣]/g, ''); // 모든 한글 제거
    };
    
    const normalizedItemName = normalizeItemName(itemName);
    console.log('🔥 normalizedItemName:', normalizedItemName);
    
    const isLinkingCord = normalizedItemName === 'linkingcord' || normalizedItemName === 'linkedcord';
    const isPartner = Boolean(pokemon?.isPartner);
    const evolution = findEvolutionForPokemon(pokemon, (evo) => {
      // 일반 아이템 진화 (누구나)
      if (evo.condition.type === 'item') {
        const evolItem = normalizeItemName(evo.condition.item || '');
        return evolItem === normalizedItemName;
      }
      // 지닌 물건 없이 그냥 교환만 하면 되는 진화는, 링킹코드로 파트너 여부와 상관없이
      // 누구나 대체 가능하다.
      if (isLinkingCord && evo.condition.type === 'trade' && !evo.condition.heldItem) {
        return true;
      }
      // 지닌 물건이 필요한 교환 진화는 파트너만 아이템으로 대체 가능
      if (!isPartner) return false;
      if (evo.condition.type === 'trade' && evo.condition.heldItem) {
        const evolItem = normalizeItemName(evo.condition.heldItem || '');
        return evolItem === normalizedItemName;
      }
      return false;
    });
    
    console.log('🔥 찾은 진화:', evolution);
    
    if (!evolution) {
      alert('❌ 이 아이템으로는 진화할 수 없습니다!');
      return false;
    }
    
    // ⭐ 변경: 바로 진화하지 않고 모달 표시
    const evolvedPokemonData = findEvolvedTemplateForPokemon(pokemon, evolution);
    
    if (!evolvedPokemonData) {
      alert('진화할 포켓몬 데이터를 찾을 수 없습니다!');
      return false;
    }
    
    // 마빌크 → 마휘핑: 일반 진화 모달 대신 맛(크림) 선택 모달을 띄운다
    if (evolvedPokemonData.nameEn === 'alcremie') {
      console.log('🍰 마휘핑 맛 선택 모달 표시:', pokemon.name);
      setAlcremieFlavorPending({
        pokemon,
        evolution,
        shapeId: getAlcremieShapeForItem(itemName)
      });
      return true;
    }

    console.log('✨ 진화 모달 표시:', pokemon.name, '→', evolvedPokemonData.name);

    // 진화 모달 표시 (실제 진화는 acceptEvolution에서)
    setEvolutionModal({
      show: true,
      pokemon: pokemon,
      evolution: evolution,
      fromPokemon: pokemon,
      toPokemon: evolvedPokemonData,
      isItemEvolution: true
    });

    return true;
  };

  // 진화형에서도 기존 특성을 그대로 쓸 수 있는지 확인하고, 아예 없어졌다면
  // (숨겨진 특성이었다면 진화형의 숨겨진 특성으로, 아니면 진화형의 일반 특성 중) 랜덤으로 새로 배정한다.
  const resolveEvolvedAbility = (pokemon, evolvedTemplate) => {
    const wasHidden = Boolean(pokemon.isHiddenAbility);
    const abilitiesEn = evolvedTemplate.abilitiesEn || [];
    const hiddenAbilityEn = evolvedTemplate.hiddenAbilityEn || null;
    const currentAbilityEn = pokemon.abilityEn;

    const stillHasAbility = wasHidden
      ? Boolean(currentAbilityEn) && currentAbilityEn === hiddenAbilityEn
      : Boolean(currentAbilityEn) && abilitiesEn.includes(currentAbilityEn);

    if (stillHasAbility) {
      return { ability: pokemon.ability, abilityEn: currentAbilityEn, isHiddenAbility: wasHidden };
    }

    if (wasHidden && hiddenAbilityEn) {
      return {
        ability: getAbilityKoreanName(hiddenAbilityEn) || hiddenAbilityEn,
        abilityEn: hiddenAbilityEn,
        isHiddenAbility: true,
      };
    }

    if (abilitiesEn.length > 0) {
      const randomAbilityEn = abilitiesEn[Math.floor(Math.random() * abilitiesEn.length)];
      return {
        ability: getAbilityKoreanName(randomAbilityEn) || randomAbilityEn,
        abilityEn: randomAbilityEn,
        isHiddenAbility: false,
      };
    }

    // 진화형에 특성 정보 자체가 없으면 기존 값 유지
    return { ability: pokemon.ability, abilityEn: currentAbilityEn, isHiddenAbility: wasHidden };
  };

  // 진화 실행 (imageOverrides: 마휘핑 맛처럼 진화 결과 이미지를 직접 지정해야 할 때 사용)
  const performEvolution = async (pokemon, evolution, imageOverrides = {}) => {
    const evolvedTemplate = findEvolvedTemplateForPokemon(pokemon, evolution);

    if (!evolvedTemplate) {
      alert('❌ 진화 정보를 찾을 수 없습니다!');
      return false;
    }

    const evolvePokemonObject = (p) => ({
      ...p,
      number: evolvedTemplate.number,
      originalNumber: evolvedTemplate.originalNumber || evolvedTemplate.number,
      pokemonId: evolvedTemplate.number,
      name: getBaseName(evolvedTemplate),
      nameEn: evolvedTemplate.nameEn,
      species: evolvedTemplate.species || evolvedTemplate.nameEn,
      regionalForm: evolvedTemplate.regionalForm || null,
      formVariant: evolvedTemplate.formVariant || null,
      isRegionalForm: Boolean(evolvedTemplate.isRegionalForm),
      baseSpecies: evolvedTemplate.baseSpecies || null,
      baseSpeciesEn: evolvedTemplate.baseSpeciesEn || null,
      type: evolvedTemplate.type,
      type2: evolvedTemplate.type2 || null,
      abilitiesEn: evolvedTemplate.abilitiesEn || p.abilitiesEn,
      hiddenAbilityEn: evolvedTemplate.hiddenAbilityEn ?? p.hiddenAbilityEn,
      ...resolveEvolvedAbility(p, evolvedTemplate),
      ...getBaseStatPatch(evolvedTemplate),
      imageUrl: evolvedTemplate.imageUrl,
      // 플라베베/플라엣테 꽃 색깔 폼처럼 template.number가 "pokemon-form-10109" 같은
      // 비숫자 문자열인 코스메틱 폼은 이 문자열로 CDN URL을 조립하면 깨진 이미지가 된다.
      // 이런 폼은 template 자체에 이미 올바른 iconUrl/spriteUrl이 저장돼 있으니 그걸 그대로 쓴다.
      iconUrl: Number.isFinite(Number(evolvedTemplate.number))
        ? (() => { const orig = evolvedTemplate.originalNumber; const n = (orig === 710 || orig === 711) ? orig : evolvedTemplate.number; return `https://cdn.jsdelivr.net/gh/PokeAPI/sprites@master/sprites/pokemon/versions/generation-viii/icons/${n}.png`; })()
        : (evolvedTemplate.iconUrl || evolvedTemplate.imageUrl),
      spriteUrl: Number.isFinite(Number(evolvedTemplate.number))
        ? `https://cdn.jsdelivr.net/gh/PokeAPI/sprites@master/sprites/pokemon/${evolvedTemplate.number}.png`
        : (evolvedTemplate.spriteUrl || evolvedTemplate.imageUrl),
      // 닉네임이 종족명이면 제거, 커스텀 닉네임은 유지
      nickname: (p.nickname && p.nickname !== getBaseName(p) && p.nickname !== p.name && p.nickname !== p.nameEn) ? p.nickname : null,
      evolutionCancelled: false,
      ...imageOverrides
    });

    // ⭐ 클로저 스냅샷(currentUser.caughtPokemon)을 통째로 덮어쓰지 않고, updateOwnedPokemonByUniqueId로
    // Firebase의 실제 최신 데이터 위에서 진화를 적용한다. 안 그러면 (1) 아이템 사용으로 방금
    // 로컬에만 반영된 변경(예: 친밀도 보너스)이 진화 결과에서 사라지고, (2) 그 사이 다른 경로로
    // 바뀐 최신 데이터를 이 함수가 들고 있던 스냅샷으로 덮어써서 진화 자체가 되돌아갈 수 있다.
    const result = await updateOwnedPokemonByUniqueId(pokemon.uniqueId, (latestPokemon) => {
      if (!latestPokemon) return latestPokemon;
      // pokemon(모달을 띄운 시점의 로컬 객체)에만 반영돼있던 친밀도 등을 최신 데이터에 병합
      const merged = { ...latestPokemon, friendship: pokemon.friendship ?? latestPokemon.friendship };
      return evolvePokemonObject(merged);
    });

    if (!result.committed) {
      alert('❌ 진화 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      return false;
    }

    const evolvedAt = Date.now();
    const evolutionHistoryEntry = {
      id: `evolution_${pokemon.uniqueId || evolvedAt}_${evolvedAt}`,
      pokemonId: pokemon.uniqueId || null,
      fromName: pokemon.nickname || getBaseName(pokemon),
      toName: getBaseName(evolvedTemplate),
      toNameEn: evolvedTemplate.nameEn,
      toNumber: evolvedTemplate.number,
      imageUrl: evolvedTemplate.imageUrl,
      evolvedAt
    };

    updateHistoryField('evolutionHistory', evolutionHistoryEntry);

    // 홈 화면 "오늘의 진화" - evolutionHistory는 memberSummary에서 빠져 있어(잦은 액션마다
    // 전체 요약이 재전송되는 걸 막기 위함) 전체 회원을 훑어서는 만들 수 없다. 그래서 진화마다
    // "가장 최근 1건"만 이 작은 노드에 덮어써서 모든 접속자가 가볍게 구독한다. 스프라이트는
    // App.jsx가 toNameEn으로 로컬 아이콘을 찾아 렌더링 시점에 계산하므로 원본 필드만 담는다.
    set(ref(database, 'homeFeed/evolution'), {
      id: evolutionHistoryEntry.id,
      trainerName: currentUser.name || currentUser.nickname || '누군가',
      toName: evolutionHistoryEntry.toName,
      toNameEn: evolutionHistoryEntry.toNameEn,
      imageUrl: evolutionHistoryEntry.imageUrl,
      eventTime: evolvedAt
    }).catch((error) => console.error('❌ 홈 피드(진화) 갱신 실패:', error));
    return true;
  };

  // 진화 수락
  const acceptEvolution = async () => {
    if (!evolutionModal || !evolutionModal.pokemon || !evolutionModal.evolution) {
      console.log('❌ 진화 모달 정보 없음');
      return;
    }

    const { pokemon, evolution } = evolutionModal;
    const success = await performEvolution(pokemon, evolution);

    if (success) {
      const evolvedTemplate = findEvolvedTemplateForPokemon(pokemon, evolution);
      alert(`🎉 축하합니다!\n${pokemon.nickname || pokemon.name}이(가) ${evolvedTemplate.name}(으)로 진화했습니다!`);
    }

    setEvolutionModal(null);
  };
  
// 수동 진화 (포켓몬 상세에서 진화 버튼 클릭)
const manualEvolve = (pokemon) => {
  console.log('🎯 manualEvolve 호출:', pokemon.name, 'Lv.', pokemon.level);
  
  // 변함없는돌을 지니고 있으면 진화 불가
  if (pokemon.heldItem === 'everstone' || pokemon.heldItem === '변함없는돌') {
    alert('변함없는돌을 지니고 있어 진화할 수 없습니다!');
    return false;
  }
  
  const preparedPokemon = ensureStoredWurmpleEvolutionId(pokemon);
  const evolution = checkEvolution(preparedPokemon);
  console.log('🎯 진화 정보:', evolution);
  
  if (!evolution) {
    alert('아직 진화 조건을 충족하지 못했습니다!');
    return false;
  }
  
  // 모달 표시 (실제 진화는 acceptEvolution에서)
  const evolvedPokemonData = findEvolvedTemplateForPokemon(preparedPokemon, evolution);
  
  if (!evolvedPokemonData) {
    alert('진화할 포켓몬 데이터를 찾을 수 없습니다!');
    return false;
  }
  
  console.log('✨ 진화 모달 표시:', pokemon.name, '→', evolvedPokemonData.name);
  
  setEvolutionModal({
    show: true,
    pokemon: preparedPokemon,
    evolution: evolution,
    fromPokemon: preparedPokemon,
    toPokemon: evolvedPokemonData,
    isItemEvolution: false
  });
  
  return true;
};

  // 진화 거부
  const cancelEvolution = () => {
    if (!evolutionModal || !evolutionModal.pokemon) {
      setEvolutionModal(null);
      return;
    }
    
    const { pokemon, isItemEvolution } = evolutionModal;
    
    // 아이템 진화는 거부 플래그 저장하지 않음 (아이템을 다시 사용하면 다시 진화 가능)
    if (!isItemEvolution) {
      // 레벨업 진화만 거부 플래그 저장
      const updatedPokemon = currentUser.caughtPokemon.map(p => {
        if (p && p.uniqueId === pokemon.uniqueId) {
          return {
            ...p,
            evolutionCancelled: true
          };
        }
        return p;
      });
      
      updateCurrentUser({ caughtPokemon: updatedPokemon });
      alert(`${pokemon.nickname || pokemon.name}의 진화를 취소했습니다.\n포켓몬 상세 화면에서 언제든지 진화시킬 수 있습니다.`);
    } else {
      alert(`${pokemon.nickname || pokemon.name}의 진화를 취소했습니다.`);
    }
    
    setEvolutionModal(null);
  };

  // 마휘핑 맛 선택 완료 → 선택한 맛의 이미지로 바로 진화
  const chooseAlcremieFlavor = async (flavor) => {
    if (!alcremieFlavorPending || !flavor) return false;
    const { pokemon, evolution, shapeId } = alcremieFlavorPending;
    const image = getAlcremieImage(flavor.id, shapeId);

    const success = await performEvolution(pokemon, evolution, image ? {
      imageUrl: image,
      spriteUrl: image,
      iconUrl: image,
      alcremieFlavor: flavor.id,
      alcremieShape: shapeId
    } : { alcremieFlavor: flavor.id, alcremieShape: shapeId });

    if (success) {
      alert(`🎉 축하합니다!\n${pokemon.nickname || pokemon.name}이(가) ${flavor.label}(으)로 진화했습니다!`);
    }

    setAlcremieFlavorPending(null);
    return success;
  };

  const cancelAlcremieFlavor = () => {
    if (alcremieFlavorPending?.pokemon) {
      const p = alcremieFlavorPending.pokemon;
      alert(`${p.nickname || p.name}의 진화를 취소했습니다.`);
    }
    setAlcremieFlavorPending(null);
  };

  // 진화 가능한 모든 포켓몬 찾기
  const getAllEvolvablePokemon = () => {
    if (!currentUser) return [];

    return currentUser.caughtPokemon
      .filter(p => p !== null)
      .map(pokemon => ({
        pokemon,
        evolution: checkEvolution(pokemon)
      }))
      .filter(item => item.evolution !== null);
  };

  return {
    evolutionModal,
    checkEvolution,
    checkEvolutionOnLevelUp,
    evolveWithItem,
    acceptEvolution,
    cancelEvolution,
    manualEvolve,
    getAllEvolvablePokemon,
    alcremieFlavorPending,
    chooseAlcremieFlavor,
    cancelAlcremieFlavor
  };
};
